import { Resources } from 'constants/resources';

import { useState } from 'react';

import ContentAdd from '@mui/icons-material/Add';
import { red } from '@mui/material/colors';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { Identifier, WithListContext } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { Button } from 'shared/mui/Buttons';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEditDialogButton,
  ContextMenuEntityUserActionsButton,
} from 'shared/mui/ContextMenu';
import { WhiteIpAddressesTooltip } from 'shared/mui/Tooltip/WhiteIpAddressesTooltip/WhiteIpAddressesTooltip';
import { Typography } from 'shared/mui/Typography';
import { DateTimeField, NumberField, TextField, TooltipTextField } from 'shared/react-admin/Fields';
import { ListPage } from 'shared/react-admin/Pages';
import { ApiClientWhiteIp } from 'types/embedded/apiClient';
import { Permission } from 'types/embedded/permission';

import { StyledDatagrid } from './ApiClientsShowWhiteIpAddresses.styled';
import { ApiClientsShowWhiteIpAddressesCreateDialog } from './ApiClientsShowWhiteIpAddressesCreateDialog/ApiClientsShowWhiteIpAddressesCreateDialog';
import { ApiClientsShowWhiteIpAddressesDeleteDialog } from './ApiClientsShowWhiteIpAddressesDeleteDialog/ApiClientsShowWhiteIpAddressesDeleteDialog';
import { ApiClientsShowWhiteIpAddressesEditDialog } from './ApiClientsShowWhiteIpAddressesEditDialog/ApiClientsShowWhiteIpAddressesEditDialog';

type Props = {
  apiClientId?: Identifier;
};

const getRowSx = (record: ApiClientWhiteIp) =>
  record?.ip === '0.0.0.0' ? { background: red[50] } : null;

export const ApiClientsShowWhiteIpAddresses: React.FC<Props> = ({ apiClientId }) => {
  const { hasResourcePermissions, hasResourceAccess } = usePermissions();

  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const {
    record: recordEditDialog,
    handleCloseRecordModal: handleCloseEditDialog,
    openRecordModal: openEditDialog,
    handleOpenRecordModal: handleOpenEditDialog,
  } = useRecordModal<ApiClientWhiteIp>();
  const {
    record: recordDeleteDialog,
    handleCloseRecordModal: handleCloseDeleteDialog,
    openRecordModal: openDeleteDialog,
    handleOpenRecordModal: handleOpenDeleteDialog,
  } = useRecordModal<ApiClientWhiteIp>();
  return (
    <ListPage
      actions={[
        hasResourcePermissions('api_client_white_ip_addresses', [
          Permission.Full,
          Permission.Create,
        ]) && (
          <Button
            key="create"
            label="Добавить"
            onClick={() => setOpenCreateDialog(true)}
            startIcon={<ContentAdd />}
            sx={{ marginTop: '8px', padding: '3px 16px' }}
            variant="contained"
          />
        ),
      ]}
      empty={false}
      filter={{ apiClient: apiClientId }}
      headerTitle={{
        titleText: 'Список белых IP адресов',
        titleSize: 'small',
      }}
      resource="api_client_white_ip_addresses"
      sort={{ field: 'id', order: 'DESC' }}
      sx={{ marginTop: 0, marginBottom: 2 }}
    >
      <WithListContext
        render={({ data, isLoading }) => {
          const showAlert = !data?.length && !isLoading;
          if (showAlert) {
            return (
              <Alert severity="info">
                Здесь пока нет белых IP адресов - доступ этого клиента к API Kubera закрыт. Добавьте
                хотя бы один белый IP адрес, чтобы открыть доступ. Чтобы добавить белый IP адрес,
                нажмите кнопку &#34;Добавить&#34;
              </Alert>
            );
          }
          return (
            <>
              <StyledDatagrid bulkActionButtons={false} rowSx={getRowSx}>
                <TextField
                  component="div"
                  label="ID"
                  sortBy="id"
                  source="originId"
                  sx={{ marginTop: '2px' }}
                />
                <TextField label="IP адрес" source="ip" />
                <NumberField
                  label={
                    <Box alignItems="center" display="flex" gap="8px">
                      <Typography fontSize="14px" fontWeight={500}>
                        Маска
                      </Typography>
                      <WhiteIpAddressesTooltip />
                    </Box>
                  }
                  source="mask"
                />
                <TooltipTextField label="Комментарий" source="comment" />
                <DateTimeField label="Дата создания" source="createdAt" />
                <DateTimeField label="Дата редактир." source="updatedAt" />
                <ContextMenu
                  render={(record: ApiClientWhiteIp) => [
                    hasResourceAccess(Resources.UserActions) && (
                      <ContextMenuEntityUserActionsButton
                        entityName="api_client_white_ip_address"
                        key="user-action-button"
                      />
                    ),
                    hasResourcePermissions('api_client_white_ip_addresses', [
                      Permission.Full,
                      Permission.Update,
                    ]) && (
                      <ContextMenuEditDialogButton
                        key="edit-button"
                        onClick={() => {
                          handleOpenEditDialog(record);
                        }}
                      />
                    ),
                    hasResourcePermissions('api_client_white_ip_addresses', [
                      Permission.Full,
                      Permission.Delete,
                    ]) && (
                      <ContextMenuDeleteButton
                        key="delete-button"
                        onClick={() => {
                          handleOpenDeleteDialog(record);
                        }}
                      />
                    ),
                  ]}
                />
              </StyledDatagrid>
            </>
          );
        }}
      />
      <ApiClientsShowWhiteIpAddressesCreateDialog
        apiClient={apiClientId}
        onClose={() => setOpenCreateDialog(false)}
        open={openCreateDialog}
      />
      <ApiClientsShowWhiteIpAddressesEditDialog
        apiClient={apiClientId}
        onClose={handleCloseEditDialog}
        open={openEditDialog}
        record={recordEditDialog}
      />
      <ApiClientsShowWhiteIpAddressesDeleteDialog
        onClose={handleCloseDeleteDialog}
        open={openDeleteDialog}
        record={recordDeleteDialog}
      />
    </ListPage>
  );
};
